import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { IconButton } from "@mui/material";
import { DataGridPremium, GridColDef } from "@mui/x-data-grid-premium";

import CloseIcon from "assets/icons/close.svg?react";
import magistratArchiveService, {
  EMagistratArchiveJobStatus,
  IMagistratArchiveReportIssue,
  IMagistratArchiveReportStep,
} from "api/magistratArchiveService";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import Loading from "components/Loading";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import MagistratArchiveStateChip from "components/Settings/MagistratArchiveStateChip";
import { plGridLocales } from "utils/muiGridLocales";

import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

const formatUtc = (value?: string | null) =>
  value ? moment.utc(value).local().format("DD.MM.YYYY HH:mm:ss") : "—";

const gridSx = {
  border: 0,
  "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
    outline: "none !important",
  },
};

/** Raport zlecenia importu teczki z archiwum Magistratu: kroki migracji, problemy, log przebiegu. */
const MagistratArchiveReportModal: FC<Props> = ({ jobId, onClose, onRetry }) => {
  const { t } = useTranslation();
  const { data, isLoading } = magistratArchiveService.useGetReport(jobId);
  const report = data?.data;
  const job = report?.Job;

  const canRetry =
    job?.Status === EMagistratArchiveJobStatus.Failed ||
    job?.Status === EMagistratArchiveJobStatus.Cancelled;

  const stepColumns = useMemo<GridColDef<IMagistratArchiveReportStep>[]>(
    () => [
      { field: "Category", headerName: t("settings.archive.reportCategory"), width: 130 },
      { field: "Step", headerName: t("settings.archive.reportStep"), minWidth: 180, flex: 1 },
      { field: "SourceRows", headerName: t("settings.archive.reportSource"), width: 80, type: "number" },
      { field: "Inserted", headerName: t("settings.archive.reportInserted"), width: 80, type: "number" },
      { field: "Updated", headerName: t("settings.archive.reportUpdated"), width: 90, type: "number" },
      { field: "Unchanged", headerName: t("settings.archive.reportUnchanged"), width: 90, type: "number" },
      { field: "Skipped", headerName: t("settings.archive.reportSkipped"), width: 80, type: "number" },
      { field: "Conflicts", headerName: t("settings.archive.reportConflicts"), width: 80, type: "number" },
      { field: "Failed", headerName: t("settings.archive.reportFailed"), width: 70, type: "number" },
    ],
    [t],
  );

  const issueColumns = useMemo<GridColDef<IMagistratArchiveReportIssue & { id: number }>[]>(
    () => [
      {
        field: "Severity",
        headerName: t("settings.archive.reportSeverity"),
        width: 110,
        valueFormatter: (value: number) => t(`settings.archive.severity.${value}`),
      },
      { field: "Step", headerName: t("settings.archive.reportStep"), width: 160 },
      { field: "SourceKey", headerName: t("settings.archive.reportSourceKey"), width: 150 },
      { field: "Message", headerName: t("settings.archive.reportMessage"), minWidth: 260, flex: 1 },
    ],
    [t],
  );

  const steps = useMemo(
    () => (report?.Steps ?? []).map((s, i) => ({ ...s, id: i })),
    [report],
  );
  const issues = useMemo(
    () => (report?.Issues ?? []).map((s, i) => ({ ...s, id: i })),
    [report],
  );

  return (
    <ModalLayout dock={DockPosition.CENTER} showBg disableMove onEnd={onClose}>
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <span className={styles.title}>
            {t("settings.archive.reportTitle", { signature: job?.CaseSignature ?? "" })}
          </span>
          <IconButton onClick={onClose} size="small" aria-label={t("common.close")}>
            <CloseIcon className={styles.closeIcon} />
          </IconButton>
        </div>

        {isLoading || !job ? (
          <Loading />
        ) : (
          <div className={styles.body}>
            <div className={styles.summary}>
              <MagistratArchiveStateChip jobStatus={job.Status} />
              <span className={styles.caseTitle}>{job.CaseTitle}</span>
            </div>
            <div className={styles.meta}>
              <span>
                {t("settings.archive.reportRequestedBy", {
                  user: job.RequestedByLabel ?? "—",
                  date: formatUtc(job.CreationDate),
                })}
              </span>
              <span>
                {t("settings.archive.reportRun", {
                  start: formatUtc(job.StartedAt),
                  end: formatUtc(job.FinishedAt),
                  attempts: job.Attempts,
                })}
              </span>
            </div>
            {job.Error && <div className={styles.error}>{job.Error}</div>}

            <div className={styles.sectionTitle}>{t("settings.archive.reportSteps")}</div>
            <div className={styles.stepsGrid}>
              <DataGridPremium
                sx={gridSx}
                localeText={plGridLocales}
                rows={steps}
                columns={stepColumns}
                columnHeaderHeight={30}
                rowHeight={32}
                hideFooter
                disableRowSelectionOnClick
              />
            </div>

            <div className={styles.sectionTitle}>
              {t("settings.archive.reportIssues", { count: issues.length })}
            </div>
            {issues.length > 0 && (
              <div className={styles.issuesGrid}>
                <DataGridPremium
                  sx={gridSx}
                  localeText={plGridLocales}
                  rows={issues}
                  columns={issueColumns}
                  columnHeaderHeight={30}
                  rowHeight={32}
                  hideFooter
                  disableRowSelectionOnClick
                />
              </div>
            )}

            {report?.Log && (
              <>
                <div className={styles.sectionTitle}>{t("settings.archive.reportLog")}</div>
                <pre className={styles.log}>{report.Log}</pre>
              </>
            )}
          </div>
        )}

        <div className={styles.footer}>
          <HollowButton text={t("common.close")} onClick={onClose} />
          {canRetry && job && (
            <Button
              text={t("settings.archive.retry")}
              onClick={() => {
                onRetry(job);
                onClose();
              }}
            />
          )}
        </div>
      </div>
    </ModalLayout>
  );
};

export default MagistratArchiveReportModal;
